import { type LucideIcon } from "lucide-react";
import { type ComponentProps } from "react";

import { cn } from "@/utils/shadcn-ui";

import { Tooltip } from "./base/tooltip";
import { Button } from "./button";

/**
 * Icon-only action button. `bare` (the default) is a brand-primary (purple)
 * icon with no chrome, styled like the copyable-cell affordance; `subtle` puts
 * the icon in the outlined {@link Button} used by toolbars. Pass `className` to
 * override the color or add spacing. When a `label` is given it becomes the
 * accessible name and a hover tooltip; without one the button renders bare (no
 * tooltip). Use for compact controls such as table row actions and dialog close
 * buttons. Remaining props (`disabled`, `ref`, `aria-*`, …) land on the
 * underlying button, so it also works as a Radix `asChild` trigger child. Note
 * that `disabled` differs by variant: `bare` gets the native attribute (no tab
 * stop, and no hover, so no tooltip either), while `subtle` inherits
 * {@link Button}'s `aria-disabled`, which keeps both.
 */
export function IconButton({
  icon: Icon,
  label,
  tooltip,
  variant = "bare",
  size = variant === "subtle" ? 16 : 14,
  className,
  ...props
}: {
  icon: LucideIcon;
  label?: string;
  /**
   * Hover text, when it should differ from the accessible name. Defaults to
   * `label`. Use it where the name has to identify the row it acts on ("Remove
   * Bisoprolol", so a screen reader is not left with five buttons called
   * "Remove") but the tooltip only needs the verb. Pass `null` to suppress the
   * tooltip, e.g. when the caller wraps the button in an explanatory one.
   */
  tooltip?: string | null;
  variant?: "bare" | "subtle";
  /**
   * Icon edge length in pixels, for `bare` only: the outlined button sizes any
   * svg it contains itself, so `subtle` icons are always 16px.
   */
  size?: number;
} & Omit<ComponentProps<"button">, "size" | "children">) {
  const icon = (
    <Icon
      size={size}
      // The outlined button sits on the page background, where purple would
      // read as a link; its icons take the regular foreground color.
      className={variant === "subtle" ? "text-foreground" : undefined}
    />
  );

  const button =
    variant === "subtle" ? (
      <Button
        aria-label={label}
        {...props}
        variant="subtle"
        size="sm"
        className={className}
      >
        {icon}
      </Button>
    ) : (
      <button
        type="button"
        aria-label={label}
        {...props}
        className={cn(
          "text-brand-primary hover:text-brand-primary/80 inline-flex shrink-0 cursor-pointer items-center bg-transparent p-0 disabled:cursor-default disabled:opacity-50",
          className,
        )}
      >
        {icon}
      </button>
    );

  // Only wrap in a tooltip when there's something to show; otherwise the button
  // renders bare (no Radix tooltip machinery, no provider dependency).
  const hover = tooltip === undefined ? label : tooltip;
  return hover ? <Tooltip content={hover}>{button}</Tooltip> : button;
}
